import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TakeUntilDestroy } from '@app/shared/decorators/take-until-destroy.decorator';
import { LimitedTimeEvent } from '@app/shared/models/limitedTimeEvent.model';
import { Modal, ModalConfig, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { ShopOffersItem } from '@app/shared/models/shop-offers.model';
import { LoggerService } from '@app/shared/services/logger.service';
import { EventManager } from '@app/shared/utils/event-manager';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { BoxComponent } from '@app/shared/components/liveops/box/box.component';
import { AnimatedTimerComponent } from '@app/shared/components/animated-timer/animated-timer.component';
import { ButtonComponent } from '@app/shared/components/button/button.component';
import { SharedModule } from '@app/shared/shared.module';
import { EventsRepository } from '@app/shared/state/events/events.repository';
import { ShopRatesRepository } from '@app/shared/state/shop-rates/shop-rates.repository';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-randomprize-start-modal',
  templateUrl: './randomprize-start.component.html',
  styleUrls: ['./randomprize-start.component.scss'],
  standalone: true,
  imports: [BoxComponent, AnimatedTimerComponent, ButtonComponent, SharedModule],
})
export class RandomPrizeStartModalComponent implements OnInit {
  componentDestroy: () => Observable<unknown>;

  private readonly eventManager = inject(EventManager);
  private readonly eventsRepo = inject(EventsRepository);
  private readonly shopRatesRepo = inject(ShopRatesRepository);
  private readonly translateService = inject(TranslateService);
  public readonly logger = inject(LoggerService);
  protected readonly randomprizeStartModalData = inject<ModalConfig>(MAT_DIALOG_DATA);

  event = signal<LimitedTimeEvent | null>(null);
  randomprizeShopItem = signal<ShopOffersItem | null>(null);
  randomprizeMinPurchase = signal<string>('0');
  randomprizeCurrencySymbol = signal<string>('');
  lang = signal<string>('');

  hasMinPurchase = computed(() => Number(this.randomprizeMinPurchase()) !== 0);

  rewardBonus: number;

  ngOnInit(): void {
    const evt = this.eventsRepo.getEventById(this.randomprizeStartModalData.properties.eventId);

    this.event.set(evt);
    this.lang.set(this.translateService.currentLang);

    this.calculateMinPurchase();
  }

  openRandomPrize(): void {
    this.eventManager.openModal(
      new Modal(NonPersistedModalName.RandomPrizeModalComponent, ModalType.View, this.randomprizeStartModalData)
    );

    this.closeModal();
  }

  closeModal(): void {
    this.eventManager.closeModal(
      new Modal(this.randomprizeStartModalData.id, ModalType.Persisted, this.randomprizeStartModalData)
    );
  }

  private calculateMinPurchase(): void {
    const item = this.shopRatesRepo
      .getShopRates()
      .Coins?.find((rate) => rate.sku === this.randomprizeStartModalData.properties.sku);

    if (!item) {
      this.randomprizeShopItem.set(null);
      this.randomprizeMinPurchase.set('0');
      this.randomprizeCurrencySymbol.set('');
      return;
    }

    this.randomprizeShopItem.set(item);
    this.randomprizeMinPurchase.set(item.amount);
    this.randomprizeCurrencySymbol.set(item.currencySymbol);
  }
}
